import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

// Ported from the original design component — framework-agnostic engine that
// drives the WebGL route grid. React just supplies DOM nodes + live props.
export class RouteGridEngine {
  constructor(els, getProps, assetBase) {
    this.els = els;
    this._getProps = getProps || (() => ({}));
    this.assetBase = assetBase || '';
  }
  get props() { return this._getProps(); }
  dispose() {
    if (this._hoverRAF) cancelAnimationFrame(this._hoverRAF);
    clearTimeout(this._rt);
    window.removeEventListener('resize', this._onResize);
    const el = this.els.gl;
    if (el) {
      el.removeEventListener('pointermove', this._onMove);
      el.removeEventListener('pointerleave', this._onLeave);
      el.removeEventListener('click', this._onClick);
    }
    try { this.renderer && this.renderer.dispose(); } catch (e) {}
  }

  // closed-eye pair (from uploads/eye.svg), viewBox 0 0 156.7 54.14
  EYE_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 156.7 54.14" style="width:100%;height:100%;display:block"><path fill="currentColor" d="M66.03,7.74c-5.62,13.16-18.49,21.66-32.79,21.66S6.07,20.9.45,7.74C-.76,4.92.55,1.65,3.38.45c2.82-1.2,6.09.1,7.3,2.93,3.87,9.05,12.72,14.91,22.56,14.91s18.7-5.85,22.56-14.91c1.21-2.82,4.47-4.14,7.3-2.93,2.82,1.21,4.14,4.47,2.93,7.3ZM156.16,34.53c-5.66,11.92-18.49,19.61-32.7,19.61s-27.04-7.7-32.7-19.61c-1.32-2.77-.14-6.09,2.64-7.41,2.78-1.31,6.09-.14,7.41,2.64,3.83,8.06,12.72,13.27,22.65,13.27s18.83-5.21,22.66-13.27c1.32-2.78,4.64-3.96,7.41-2.64,2.77,1.32,3.95,4.63,2.64,7.41Z"></path></svg>';

  // ---- model catalogue: shapeKey -> list of variant files ----
  MODELS = {
    p1:    ['obj/p1/p1_00_01_sphere.glb','obj/p1/p1_00_02_cloud.glb','obj/p1/p1_00_03_sun.glb'],
    p2:    ['obj/p2/p2_00_01_hex.glb','obj/p2/p2_00_02_hourglass.glb','obj/p2/p2_00_03_dango.glb'],
    p3_00: ['obj/p3/p3_00_01_hex.glb','obj/p3/p3_00_02_screw.glb','obj/p3/p3_00_03_line.glb','obj/p3/p3_00_04_sun.glb'],
    p3_01: ['obj/p3/p3_01_01_hex.glb','obj/p3/p3_01_02_screw.glb','obj/p3/p3_01_03_line.glb'],
    p4_00: ['obj/p4/p4_00_01_candy.glb','obj/p4/p4_00_02_screw.glb','obj/p4/p4_00_03_deco.glb','obj/p4/p4_00_04_sun.glb'],
    p4_01: ['obj/p4/p4_01_01_candy.glb','obj/p4/p4_01_02_screw.glb','obj/p4/p4_01_03_deco.glb','obj/p4/p4_01_04_sun.glb'],
    p4_02: ['obj/p4/p4_02_01_candy.glb','obj/p4/p4_02_02_screw.glb','obj/p4/p4_02_03_deco.glb','obj/p4/p4_02_04_sun.glb'],
    p4_03: ['obj/p4/p4_03_01_candy.glb','obj/p4/p4_03_02_screw.glb','obj/p4/p4_03_03_deco.glb','obj/p4/p4_03_04_sun.glb'],
  };

  mount() {
    // 8 planar grid symmetries (D4): [a,b,c,d] acting on (x,z)
    const rots = [[1,0,0,1],[0,-1,1,0],[-1,0,0,-1],[0,1,-1,0]];
    this.SYMS = [];
    for (const r of rots) {
      this.SYMS.push(r);                                   // rotation
      this.SYMS.push([-r[0], r[1], -r[2], r[3]]);          // rotation ∘ flipX
    }

    this.init();
  }

  ensureThree(cb) { cb(); }

  init() {
    
    const gl = this.els.gl;
    this.renderer = new THREE.WebGLRenderer({ canvas: gl, antialias: true, alpha: true, preserveDrawingBuffer: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this.scene = new THREE.Scene();
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.01, 5000);
    this.camera.up.set(0, 0, -1);
    this.footprints = {};

    // lighting: a low, raking key so relief across the top surface reads as
    // cel bands; soft ambient + rim keep shadow side from going fully black
    this.scene.add(new THREE.AmbientLight(0xffffff, 0.2));
    const key = new THREE.DirectionalLight(0xffffff, 1.7);
    key.position.set(0.62, 0.4, 0.26);
    this.scene.add(key);
    const rim = new THREE.DirectionalLight(0xffffff, 0.5);
    rim.position.set(-0.5, 0.5, -0.55);
    this.scene.add(rim);

    this.routeGroup = new THREE.Group();
    this.scene.add(this.routeGroup);

    this.loader = new GLTFLoader();
    this.cache = new Map();
    this.seed = Math.random() * 1e5;
    this.isResizing = false;

    // preload every unique model once, then build
    const urls = [...new Set(Object.values(this.MODELS).flat())];
    // show the flat preview immediately as a loading state
    this.drawFlat();
    this.showFlat(true);
    Promise.all(urls.map(u => this.loadModel(u)))
      .then(() => { this.computeFootprints(); this.build(); this.showFlat(false); })
      .catch(e => console.error('model load failed', e && e.message, e && e.stack));

    this._onResize = () => this.handleResize();
    window.addEventListener('resize', this._onResize);

    // hover picking: enlarge the object under the cursor
    this._raycaster = new THREE.Raycaster();
    this._onMove = (ev) => this.handleHover(ev);
    this._onLeave = () => { this.setHovered(null); if (this.els.gl) this.els.gl.style.cursor = 'default'; };
    const el = this.els.gl;
    if (el) {
      el.style.pointerEvents = 'auto';
      el.addEventListener('pointermove', this._onMove);
      el.addEventListener('pointerleave', this._onLeave);
      this._onClick = (ev) => this.handleClick(ev);
      el.addEventListener('click', this._onClick);
    }
  }

  handleClick(ev) {
    if (!this.camera || !this._eyeObj) return;
    const el = this.els.gl;
    const r = el.getBoundingClientRect();
    const nx = ((ev.clientX - r.left) / r.width) * 2 - 1;
    const ny = -(((ev.clientY - r.top) / r.height) * 2 - 1);
    this._raycaster.setFromCamera({ x: nx, y: ny }, this.camera);
    const hits = this._raycaster.intersectObjects(this.routeGroup.children, true);
    if (!hits.length) return;
    let o = hits[0].object;
    while (o && !o.userData.isRouteObj) o = o.parent;
    if (o && o === this._eyeObj) { this.openAbout(); return; }
    if (o && o.userData.isRouteObj) this.swapVariant(o);
  }

  // click a (non-eye) piece -> morph it to a different variant of the same
  // shape key, keeping its exact placement (shared footprint per key)
  swapVariant(obj) {
    
    const key = obj.userData.key;
    const variants = this.MODELS[key];
    if (!variants || variants.length < 2) return;
    // always advance to the next variant so every click visibly changes it
    const nvi = (obj.userData.vi + 1) % variants.length;
    const src = this.cache.get(variants[nvi]);
    if (!src) return;
    const next = src.clone(true);
    next.traverse(o => { if (o.isMesh) { o.material = this.mat; o.castShadow = false; } });
    next.matrixAutoUpdate = false;
    next.userData = Object.assign({}, obj.userData, { vi: nvi });
    // apply current base matrix (with the object's live hover scale)
    const c = next.userData.center;
    const f = obj.userData.scaleF || 1;
    const s = new THREE.Matrix4().makeScale(f, f, f);
    const m = new THREE.Matrix4().makeTranslation(c.x, c.y, c.z)
      .multiply(s)
      .multiply(new THREE.Matrix4().makeTranslation(-c.x, -c.y, -c.z))
      .multiply(next.userData.baseMatrix);
    next.matrix.copy(m);
    next.matrixWorldNeedsUpdate = true;
    this.routeGroup.remove(obj);
    this.routeGroup.add(next);
    const i = this._objs.indexOf(obj);
    if (i >= 0) this._objs[i] = next;
    if (this._hovered === obj) this._hovered = next;
    this.renderer.render(this.scene, this.camera);
  }

  openAbout = () => {
    if (this.els.about) {
      this.els.about.style.boxShadow = '-12px 0 60px rgba(0,0,0,0.22)';
      this.els.about.style.transform = 'translateX(0)';
    }
  };
  closeAbout = () => {
    const el = this.els.about;
    if (!el) return;
    el.style.transform = 'translateX(100%)';
    // drop the shadow once closed so it doesn't bleed onto the grid at rest
    const done = () => { el.style.boxShadow = 'none'; el.removeEventListener('transitionend', done); };
    el.addEventListener('transitionend', done);
  };

  handleHover(ev) {
    if (!this.camera || !this._objs || !this._objs.length) return;
    const el = this.els.gl;
    const r = el.getBoundingClientRect();
    const nx = ((ev.clientX - r.left) / r.width) * 2 - 1;
    const ny = -(((ev.clientY - r.top) / r.height) * 2 - 1);
    this._raycaster.setFromCamera({ x: nx, y: ny }, this.camera);
    const hits = this._raycaster.intersectObjects(this.routeGroup.children, true);
    let target = null;
    if (hits.length) {
      let o = hits[0].object;
      while (o && !o.userData.isRouteObj) o = o.parent;
      target = o || null;
    }
    this.setHovered(target);
  }

  setHovered(obj) {
    if (this._hovered === obj) return;
    const prev = this._hovered;
    this._hovered = obj;
    if (obj) {
      // grow immediately; cancel any pending shrink on this one
      if (obj.userData._shrinkT) { clearTimeout(obj.userData._shrinkT); obj.userData._shrinkT = null; }
      obj.userData.scaleTarget = 1.04;
      if (this.els.gl) this.els.gl.style.cursor = (obj === this._eyeObj) ? 'pointer' : 'default';
      this.startHoverAnim();
    }
    if (prev && prev !== obj) {
      // delay before easing back to original size
      prev.userData._shrinkT = setTimeout(() => {
        prev.userData._shrinkT = null;
        prev.userData.scaleTarget = 1;
        this.startHoverAnim();
      }, 450);
    }
  }

  startHoverAnim() {
    if (this._hoverRAF) return;
    
    const tick = () => {
      let active = false;
      for (const obj of this._objs || []) {
        const u = obj.userData;
        const diff = u.scaleTarget - u.scaleF;
        if (Math.abs(diff) > 0.0015) { u.scaleF += diff * 0.03; active = true; }
        else u.scaleF = u.scaleTarget;
        // scale about the object's own world centre, on top of its base matrix
        const c = u.center;
        const s = new THREE.Matrix4().makeScale(u.scaleF, u.scaleF, u.scaleF);
        const m = new THREE.Matrix4().makeTranslation(c.x, c.y, c.z)
          .multiply(s)
          .multiply(new THREE.Matrix4().makeTranslation(-c.x, -c.y, -c.z))
          .multiply(u.baseMatrix);
        obj.matrix.copy(m);
        obj.matrixWorldNeedsUpdate = true;
      }
      this.renderer.render(this.scene, this.camera);
      this.placeEyes();
      if (active) { this._hoverRAF = requestAnimationFrame(tick); }
      else { this._hoverRAF = null; }
    };
    this._hoverRAF = requestAnimationFrame(tick);
  }

  loadModel(url) {
    if (this.cache.has(url)) return Promise.resolve(this.cache.get(url));
    return new Promise((resolve, reject) => {
      this.loader.load(this.assetBase + url, (g) => {
        // GLBs ship without usable vertex normals -> toon/normal shading reads
        // dead flat. Recompute them once per geometry so the surface has relief.
        g.scene.traverse(o => {
          if (o.isMesh && o.geometry) {
            o.geometry.deleteAttribute('normal');
            o.geometry.computeVertexNormals();
          }
        });
        this.cache.set(url, g.scene); resolve(g.scene);
      }, undefined, (err) => reject(err));
    });
  }

  // Classic cartoon lighting: `n` hard tone bands (dot(N,L) quantised into a
  // 1D ramp). More bands = smoother, fewer = starker poster look.
  makeToonGradient(n) {
    
    n = Math.max(2, Math.round(n || 3));
    const steps = new Uint8Array(n);
    for (let i = 0; i < n; i++) steps[i] = Math.round(72 + (255 - 72) * (i / (n - 1)));
    const tex = new THREE.DataTexture(steps, n, 1, THREE.RedFormat);
    tex.minFilter = THREE.NearestFilter;
    tex.magFilter = THREE.NearestFilter;
    tex.needsUpdate = true;
    return tex;
  }

  // ---- viewport + grid geometry ----
  dims() {
    const w = this.els.wrap.clientWidth || window.innerWidth;
    const h = this.els.wrap.clientHeight || window.innerHeight;
    return { w, h };
  }

  gridSpec(w, h) {
    const spacing = this.props.spacing ?? 130;
    const margin = spacing * 0.5;
    const cols = Math.max(2, Math.floor((w - margin * 2) / spacing) + 1);
    const rows = Math.max(2, Math.floor((h - margin * 2) / spacing) + 1);
    return { spacing, cols, rows };
  }

  rand(x) {
    const s = Math.sin(x * 12.9898 + 78.233) * 43758.5453;
    return s - Math.floor(s);
  }

  // ---- port of the Houdini route algorithm ----
  generateRoutes(cols, rows, seed) {
    const pts = [];
    const idxOf = new Map();
    for (let r = 0; r < rows; r++)
      for (let c = 0; c < cols; c++) { idxOf.set(c + ',' + r, pts.length); pts.push({ col: c, row: r }); }

    const neighbours = (i) => {
      const p = pts[i], out = [];
      const cand = [[p.col+1,p.row],[p.col-1,p.row],[p.col,p.row+1],[p.col,p.row-1]];
      for (const [c, r] of cand) { const j = idxOf.get(c + ',' + r); if (j != null) out.push(j); }
      return out;
    };

    let unvisited = pts.map((_, i) => i);
    const routePts = new Set();
    const routes = [];
    let stepSeed = 0;

    while (unvisited.length > 0) {
      const prim = [];
      stepSeed += 0.1;
      const maxSteps = Math.floor(this.rand(seed + stepSeed) * 3) + 1; // 1..3 -> routes of 2..4 pts (or fewer on dead-end)
      const startIdx = Math.floor(this.rand(seed + stepSeed * 7.31) * unvisited.length);
      let current = unvisited[startIdx];
      prim.push(current); routePts.add(current);

      for (let step = 0; step < maxSteps; step++) {
        const cands = neighbours(current).filter(p => !routePts.has(p));
        if (cands.length === 0) break;
        const next = cands[Math.floor(this.rand(seed + current + step) * cands.length)];
        prim.push(next); routePts.add(next); current = next;
      }
      routes.push(prim);
      unvisited = unvisited.filter(p => !routePts.has(p));
    }
    return { pts, routes };
  }

  // ---- self-calibrating shape matching via occupied-cell footprints ----
  // Raycast each model straight down to read exactly which grid cells its
  // geometry occupies. No hand-authored orientation guesses -> a route is
  // matched to the model whose footprint maps onto the route's cells under
  // one of the 8 grid symmetries, so the rendered geometry always lands
  // precisely on the route and never bulges into a neighbour.
  computeFootprints() {
    
    this.footprints = {};
    const rc = new THREE.Raycaster();
    const down = new THREE.Vector3(0, -1, 0);
    for (const [key, variants] of Object.entries(this.MODELS)) {
      const scene = this.cache.get(variants[0]);
      if (!scene) continue;
      scene.updateMatrixWorld(true);
      const box = new THREE.Box3().setFromObject(scene);
      const yTop = box.max.y + 1;
      const expected = Number(key[1]); // p1->1, p2->2, p3_*->3, p4_*->4
      const hit = (x, z) => { rc.set(new THREE.Vector3(x, yTop, z), down); return rc.intersectObject(scene, true).length > 0; };

      // organic bulges make the bbox larger than point±0.5, so the point lattice
      // origin is unknown. Try all 4 half-integer parities and keep the sampling
      // that lands exactly `expected` solid points — that's the true footprint.
      let cells = null;
      const parities = [0, 0.5];
      for (const px of parities) {
        for (const pz of parities) {
          const got = [];
          for (let x = Math.floor(box.min.x) + px; x <= box.max.x; x += 1) {
            for (let z = Math.floor(box.min.z) + pz; z <= box.max.z; z += 1) {
              if (x >= box.min.x && z >= box.min.z && hit(x, z)) got.push([x, z]);
            }
          }
          if (got.length === expected) { cells = got; break; }
        }
        if (cells) break;
      }
      if (!cells) { console.warn('footprint miss', key); continue; }
      const xs = cells.map(c => c[0]), zs = cells.map(c => c[1]);
      const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
      const cz = (Math.min(...zs) + Math.max(...zs)) / 2;
      // offsets in half-integer *2 space so all values stay integer under symmetry
      const offsets = cells.map(c => [Math.round(2 * (c[0] - cx)), Math.round(2 * (c[1] - cz))]);
      this.footprints[key] = { count: cells.length, cx, cz, offsets };
    }
  }

  matchRoute(pts, route) {
    const P = route.map(i => pts[i]);
    const cols = P.map(p => p.col), rows = P.map(p => p.row);
    const rcx = (Math.min(...cols) + Math.max(...cols)) / 2;
    const rcz = (Math.min(...rows) + Math.max(...rows)) / 2;
    const routeSet = new Set(P.map(p => Math.round(2 * (p.col - rcx)) + ',' + Math.round(2 * (p.row - rcz))));
    const n = route.length;
    const footprints = this.footprints || {};
    const candidates = Object.keys(footprints).filter(k => footprints[k] && footprints[k].count === n);

    for (const k of candidates) {
      const fp = footprints[k];
      for (const S of this.SYMS || []) {
        const seen = new Set();
        for (const [dx, dz] of fp.offsets) {
          seen.add((S[0] * dx + S[1] * dz) + ',' + (S[2] * dx + S[3] * dz));
        }
        if (seen.size === routeSet.size && [...seen].every(o => routeSet.has(o))) {
          return { key: k, S, fp };
        }
      }
    }

    const fallbackKey = Object.keys(this.MODELS || {}).find(k => k === `p${n}` || k.startsWith(`p${n}_`)) || `p${n}`;
    return { key: fallbackKey, S: [1, 0, 0, 1], fp: footprints[fallbackKey] || null };
  }

  // ---- build the 3D scene ----
  build() {
    
    const { w, h } = this.dims();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(w, h, false);

    const { spacing, cols, rows } = this.gridSpec(w, h);
    this._ppu = spacing * 0.9; // world->px scale, needed for line thickness
    const { pts, routes } = this.generateRoutes(cols, rows, this.seed);

    // shared cel-shaded ink material (banded toon lighting). Base is a
    // lightened tint of the ink so lit facets read as highlights while the
    // darkest cel band lands back on the true ink tone -> the 3D form shows.
    // flat unlit silhouette — one solid colour, no shading or detail
    const ink = this.props.inkColor ?? '#333333';
    if (!this.mat) {
      this.mat = new THREE.MeshBasicMaterial({ side: THREE.DoubleSide, polygonOffset: true, polygonOffsetFactor: 1, polygonOffsetUnits: 1 });
    }
    this.mat.color = new THREE.Color(ink);

    // clear old
    while (this.routeGroup.children.length) {
      const c = this.routeGroup.children.pop();
      this.routeGroup.remove(c);
    }

    this._suns = [];
    this._eyeCandidates = [];
    this._objs = [];
    this._hovered = null;
    for (const route of routes) {
      const { key, S, fp } = this.matchRoute(pts, route);
      const variants = this.MODELS[key];
      const vi = Math.floor(this.rand(this.seed + route[0] * 3.7 + route.length * 11.1) * variants.length) % variants.length;
      const src = this.cache.get(variants[vi]);
      if (!src) continue;
      const obj = src.clone(true);
      obj.traverse(o => { if (o.isMesh) { o.material = this.mat; o.castShadow = false; } });

      // route bbox center in grid coords -> world (x=col, z=row)
      let minC = Infinity, maxC = -Infinity, minR = Infinity, maxR = -Infinity;
      for (const idx of route) { const p = pts[idx]; minC = Math.min(minC, p.col); maxC = Math.max(maxC, p.col); minR = Math.min(minR, p.row); maxR = Math.max(maxR, p.row); }
      const cx = (minC + maxC) / 2, cz = (minR + maxR) / 2;

      // shrink each piece toward its own center so adjacent routes don't touch/merge
      const k = this.props.pieceScale ?? 0.86;
      // world = translate(routeCenter) · (S·k) · translate(-modelCenter)
      // so the model's footprint lands exactly on the route's cells
      const mcx = fp ? fp.cx : 0, mcz = fp ? fp.cz : 0;
      const linear = new THREE.Matrix4().set(
        S[0]*k, 0, S[1]*k, 0,
        0,      1, 0,      0,
        S[2]*k, 0, S[3]*k, 0,
        0,      0, 0,      1
      );
      const m = new THREE.Matrix4()
        .makeTranslation(cx, 0, cz)
        .multiply(linear)
        .multiply(new THREE.Matrix4().makeTranslation(-mcx, 0, -mcz));
      obj.matrixAutoUpdate = false;
      obj.matrix.copy(m);
      obj.matrixWorldNeedsUpdate = true;
      obj.userData.baseMatrix = m.clone();
      obj.userData.center = new THREE.Vector3(cx, 0, cz);
      obj.userData.scaleF = 1;
      obj.userData.scaleTarget = 1;
      obj.userData.isRouteObj = true;
      obj.userData.key = key;
      obj.userData.vi = vi;
      this.routeGroup.add(obj);
      this._objs.push(obj);

      // remember candidates for the eye overlay: prefer p1 suns, fall back to
      // any p1 object. We pick exactly one after the whole grid is built.
      if (key === 'p1') {
        const half = ((Math.min(maxC - minC, maxR - minR) + 1) / 2) * k;
        this._eyeCandidates.push({ cx, cz, half, obj, isSun: variants[vi].includes('p1_00_03_sun') });
      }
    }

    // camera: fixed flat top-down orthographic plan
    const ppu = spacing * 0.9;            // pixels per world unit — uniform on both axes
    const tilt = 0;
    const az   = 0;
    const cosT = 1;
    const fw = w / ppu * 1.06;                    // small margins for object height
    const fh = h / ppu / cosT * 1.06;             // Z foreshortens by cosT — compensate
    const gcx = (cols - 1) / 2, gcz = (rows - 1) / 2;
    this.camera.left = -fw / 2; this.camera.right = fw / 2;
    this.camera.top = fh / 2; this.camera.bottom = -fh / 2;
    const dist = 900;
    this.camera.position.set(
      gcx + dist * Math.sin(tilt) * Math.sin(az),
      dist * cosT,
      gcz + dist * Math.sin(tilt) * Math.cos(az)
    );
    this.camera.lookAt(gcx, 0, gcz);
    this.camera.updateProjectionMatrix();
    // choose exactly one target for the eyes: a p1 sun if any exist, else a
    // random p1 object; selected deterministically from the current seed
    const cands = this._eyeCandidates || [];
    const suns = cands.filter(c => c.isSun);
    const pool = suns.length ? suns : cands;
    if (pool.length) {
      const pick = pool[Math.floor(this.rand(this.seed + 91.7) * pool.length) % pool.length];
      this._suns = [pick];
      this._eyeObj = pick.obj;
    } else { this._eyeObj = null; }

    this.renderer.setClearColor(new THREE.Color(this.props.bgColor ?? '#ffffff'), 1);
    this.renderer.render(this.scene, this.camera);
    if (this.els.line) {
      const lc = this.els.line;
      lc.getContext('2d').clearRect(0, 0, lc.width, lc.height);
    }
    this.placeEyes();
  }

  // Overlay closed-eye SVGs on each p1 sun, positioned by projecting the
  // object's world centre to screen space (camera is fixed top-down ortho).
  placeEyes() {
    
    const layer = this.els.eyes, wrap = this.els.wrap;
    if (!layer || !wrap || !this._suns) return;
    layer.innerHTML = '';
    const W = wrap.clientWidth, H = wrap.clientHeight;
    const eyeColor = this.props.bgColor ?? '#ffffff';
    const v = new THREE.Vector3();
    for (const s of this._suns) {
      v.set(s.cx, 0, s.cz).project(this.camera);
      const px = (v.x * 0.5 + 0.5) * W, py = (1 - (v.y * 0.5 + 0.5)) * H;
      // pixel radius from projecting a half-width offset
      const e = new THREE.Vector3(s.cx + s.half, 0, s.cz).project(this.camera);
      const rpx = Math.abs((e.x * 0.5 + 0.5) * W - px);
      const sf = this._eyeObj ? (this._eyeObj.userData.scaleF || 1) : 1;
      const ew = rpx * 1.15 * sf;       // eye-pair width relative to sun radius
      const eh = ew * (54.14 / 156.7);
      const el = document.createElement('div');
      el.style.cssText = `position:absolute; left:${px}px; top:${py}px; width:${ew}px; height:${eh}px; transform:translate(-50%,-50%); color:${eyeColor};`;
      el.innerHTML = this.EYE_SVG;
      layer.appendChild(el);
    }
  }

  // Ink line pass. Renders an offscreen NORMAL + DEPTH buffer of the scene and
  // edge-detects it, so we get two kinds of line, both independent of ink
  // colour: the outer silhouette (depth/coverage discontinuity) AND interior
  // crease lines where the surface bends sharply (the twist grooves, facets,
  // etc. that make each model read as a crafted 3D object, not a flat blob).
  compositeOutline() {
    
    const gl = this.els.gl, lc = this.els.line;
    if (!gl || !lc) return;
    const W = gl.width, H = gl.height;
    lc.width = W; lc.height = H;
    const lctx = lc.getContext('2d');
    lctx.clearRect(0, 0, W, H);
    if (this.props.showLines === false) return;

    // ---- offscreen normal pass ----
    if (!this.normalRT || this.normalRT.width !== W || this.normalRT.height !== H) {
      if (this.normalRT) this.normalRT.dispose();
      this.normalRT = new THREE.WebGLRenderTarget(W, H);
    }
    if (!this.normalMat) this.normalMat = new THREE.MeshNormalMaterial({ side: THREE.DoubleSide });

    this.scene.overrideMaterial = this.normalMat;
    this.renderer.setRenderTarget(this.normalRT);
    this.renderer.setClearColor(0x000000, 0);   // transparent -> alpha marks coverage
    this.renderer.clear();
    this.renderer.render(this.scene, this.camera);
    const raw = new Uint8Array(W * H * 4);
    this.renderer.readRenderTargetPixels(this.normalRT, 0, 0, W, H, raw);
    // GL readback is bottom-up; flip to top-down so it aligns with the canvas
    const buf = new Uint8Array(W * H * 4);
    const rowBytes = W * 4;
    for (let y = 0; y < H; y++) {
      buf.set(raw.subarray((H-1-y)*rowBytes, (H-y)*rowBytes), y*rowBytes);
    }
    this.renderer.setRenderTarget(null);
    this.scene.overrideMaterial = null;
    this.renderer.setClearColor(new THREE.Color(this.props.bgColor ?? '#ffffff'), 1);

    const N = W * H;
    // coverage mask from normal-pass alpha
    const mask = new Uint8Array(N);
    for (let i = 0; i < N; i++) mask[i] = buf[i*4+3] > 127 ? 1 : 0;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const t = Math.max(1, Math.round((this.props.lineWeight ?? 3) * dpr));
    const d = Math.max(2, Math.round(2.5 * dpr));      // normal-sampling stride
    const cosThresh = 0.6;                             // only hard structural creases

    // seed line pixels: silhouette (mask edge) + only strong interior creases,
    // so the read stays clean like classic cel shading (tone + outline)
    const seed = new Uint8Array(N);
    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        const i = y * W + x;
        if (!mask[i]) continue;
        // silhouette: any 4-neighbour is background
        if (x === 0 || x === W-1 || y === 0 || y === H-1 ||
            !mask[i-1] || !mask[i+1] || !mask[i-W] || !mask[i+W]) { seed[i] = 1; continue; }
        // optional interior crease ink (off by default = clean cel look)
        if (!this.props.inkCreases) continue;
        const ix = x + d < W ? i + d : i - d;
        const iy = y + d < H ? i + d*W : i - d*W;
        if (!mask[ix] || !mask[iy]) continue;
        const nx = buf[i*4]-128, ny = buf[i*4+1]-128, nz = buf[i*4+2]-128;
        const len = Math.hypot(nx, ny, nz) || 1;
        let crease = false;
        for (const j of [ix, iy]) {
          const mx = buf[j*4]-128, my = buf[j*4+1]-128, mz = buf[j*4+2]-128;
          const ml = Math.hypot(mx, my, mz) || 1;
          const dot = (nx*mx + ny*my + nz*mz) / (len*ml);
          if (dot < cosThresh) { crease = true; break; }
        }
        if (crease) seed[i] = 1;
      }
    }

    // thicken seed to the requested line weight (disk dilation, clipped to shape)
    const line = new Uint8Array(N);
    const r = Math.max(1, Math.round(t * 0.9)), r2 = r * r;
    for (let i = 0; i < N; i++) {
      if (!seed[i]) continue;
      const x = i % W, y = (i / W) | 0;
      for (let dy = -r; dy <= r; dy++) {
        const yy = y + dy; if (yy < 0 || yy >= H) continue;
        for (let dx = -r; dx <= r; dx++) {
          if (dx*dx + dy*dy > r2) continue;
          const xx = x + dx; if (xx < 0 || xx >= W) continue;
          const k = yy * W + xx;
          if (mask[k]) line[k] = 1;
        }
      }
    }

    // paint
    const out = lctx.createImageData(W, H);
    const ln = new THREE.Color(this.props.lineColor ?? '#111111');
    const R = Math.round(ln.r * 255), G = Math.round(ln.g * 255), B = Math.round(ln.b * 255);
    const od = out.data;
    for (let i = 0; i < N; i++) {
      if (line[i]) { od[i*4] = R; od[i*4+1] = G; od[i*4+2] = B; od[i*4+3] = 255; }
    }
    lctx.putImageData(out, 0, 0);
  }

  // ---- flat 2D preview (shown while resizing / loading) ----
  drawFlat() {
    const cv = this.els.flat;
    const { w, h } = this.dims();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    cv.width = w * dpr; cv.height = h * dpr;
    const ctx = cv.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const { spacing, cols, rows } = this.gridSpec(w, h);
    const seed = this.flatSeed ?? this.seed;
    const { pts, routes } = this.generateRoutes(cols, rows, seed);
    const ppu = spacing * 0.9;
    const gcx = (cols - 1) / 2, gcy = (rows - 1) / 2;
    const X = p => w / 2 + (p.col - gcx) * ppu, Y = p => h / 2 + (p.row - gcy) * ppu;

    const ink = this.props.inkColor ?? '#1b1b1b';
    ctx.fillStyle = this.props.bgColor ?? '#ffffff';
    ctx.fillRect(0, 0, w, h);

    ctx.strokeStyle = ink; ctx.globalAlpha = 0.35; ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    for (const route of routes) {
      if (route.length < 2) continue;
      ctx.beginPath(); ctx.moveTo(X(pts[route[0]]), Y(pts[route[0]]));
      for (let i = 1; i < route.length; i++) ctx.lineTo(X(pts[route[i]]), Y(pts[route[i]]));
      ctx.stroke();
    }
    ctx.globalAlpha = 0.7; ctx.fillStyle = ink;
    for (const p of pts) { ctx.beginPath(); ctx.arc(X(p), Y(p), 4, 0, Math.PI * 2); ctx.fill(); }
    ctx.globalAlpha = 1;
  }

  showFlat(on) {
    if (this.els.flat) this.els.flat.style.opacity = on ? '1' : '0';
    if (this.els.gl) this.els.gl.style.opacity = on ? '0' : '1';
    if (this.els.eyes) this.els.eyes.style.opacity = on ? '0' : '1';
  }

  shuffle = () => {
    if (this._shuffling) return;
    this._shuffling = true;
    // 1) draw the route preview for the new layout and fade it in
    this.seed = Math.random() * 1e5;
    this.flatSeed = this.seed;
    this.drawFlat();
    this.showFlat(true);
    // 2) after the route shows, build the models and fade back to them
    setTimeout(() => {
      this.build();
      this.showFlat(false);
      this._shuffling = false;
    }, 320);
  };

  handleResize() {
    if (!this.isResizing) { this.isResizing = true; this.flatSeed = Math.random() * 1e5; }
    this.drawFlat();
    this.showFlat(true);
    clearTimeout(this._rt);
    this._rt = setTimeout(() => {
      this.isResizing = false;
      this.seed = Math.random() * 1e5; // new random routes for the new size
      this.build();
      this.showFlat(false);
    }, 240);
  }
}
